import React, { useState, useEffect } from 'react';
import { Layout, Typography, Card, Row, Col, Button, Carousel, Space, Divider, Menu, message } from 'antd';
import { 
    UserOutlined, 
    FileTextOutlined, 
    CheckCircleOutlined, 
    PhoneOutlined,
    MailOutlined,
    EnvironmentOutlined,
    MenuOutlined,
    FacebookOutlined,
    BankOutlined,
    NumberOutlined,
    LineChartOutlined,
    LogoutOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AdmissionNews from './Section/AdmissionNews';
import ProgramList from './Section/ProgramList';
import Chatbot from './Section/Chatbot';
import AdmissionCountdown from './Section/AdmissionCountdown';
import useActivePeriod from '../../hooks/useActivePeriod';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

// Brand Colors
const colors = {
    primary: '#f79421',    // Orange
    secondary: '#262262',  // Deep Purple
    text: '#3e3e3e',      // Dark Gray
    white: '#fff',
    lightGray: '#ccc',
    blue: '#477CBF',
    background: '#f5f5f5'
};

const Home = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [menuVisible, setMenuVisible] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const { period, loading: periodLoading, hasActivePeriod } = useActivePeriod();

    // Check authentication
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (token && userData) {
            setIsAuthenticated(true);
            setUser(JSON.parse(userData));
        }
    }, []);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const carouselContent = [
        {
            title: "Đăng ký hồ sơ dự tuyển đại học chính quy 2026",
            description: "",
            image: "./anh-phoi-canh-utt.jpg"
        },
    ];

    const features = [
        {
            icon: <UserOutlined style={{ fontSize: '32px', color: colors.primary }} />,
            title: "Hơn 22.000 +",
            description: "Quy mô hơn 22.000 học viên, sinh viên"
        },
        {
            icon: <FileTextOutlined style={{ fontSize: '32px', color: colors.secondary }} />,
            title: "Hơn 40%",
            description: "Giảng viên có học hàm, học vị GS, PGS,TS"
        },
        {
            icon: <CheckCircleOutlined style={{ fontSize: '32px', color: colors.blue }} />,
            title: "Hơn 60+",
            description: "Chương trình đào tạo bậc đại học"
        },
        {
            icon: <LineChartOutlined style={{ fontSize: '32px', color: colors.blue }} />,
            title: "Trên 90%",
            description: "Sinh viên ra trường có việc làm"
        },
        {
            icon: <BankOutlined style={{ fontSize: '32px', color: colors.blue }} />,
            title: "Môi trường học tập hiện đại",
            description: "Cơ sở vật chất hiện đại, đầy đủ tiện ích"
        },
        {
            icon: <NumberOutlined style={{ fontSize: '32px', color: colors.blue }} />,
            title: "Triết lý giáo dục",
            description: "Ứng dụng - Thực học - Thực nghiệp"
        }
    ];

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        setIsAuthenticated(false);
        setUser(null);
        message.success('Đăng xuất thành công!');
        navigate('/login');
    };

    const MobileMenu = () => (
        <div
            style={{
                position: 'fixed',
                top: 64,
                left: 0,
                right: 0,
                background: colors.white,
                padding: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                display: menuVisible ? 'block' : 'none'
            }}
        >
            {isAuthenticated ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ padding: '8px', color: colors.secondary }}>
                        <UserOutlined /> {user?.name || user?.email}
                    </div>
                    <Button 
                        type="link" 
                        onClick={() => {
                            navigate('/student-profile');
                            setMenuVisible(false);
                        }}
                        style={{ color: colors.secondary, width: '100%', textAlign: 'left' }}
                    >
                        Hồ sơ dự tuyển
                    </Button>
                    <Button 
                        danger
                        onClick={() => {
                            handleLogout();
                            setMenuVisible(false);
                        }}
                        style={{ width: '100%' }}
                    >
                        <LogoutOutlined /> Đăng xuất
                    </Button>
                </Space>
            ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Button 
                        type="link" 
                        onClick={() => {
                            navigate('/login');
                            setMenuVisible(false);
                        }}
                        style={{ color: colors.secondary, width: '100%', textAlign: 'left' }}
                    >
                        Đăng nhập
                    </Button>
                    {hasActivePeriod && (
                        <Button
                            type="primary"
                            onClick={() => {
                                navigate('/register');
                                setMenuVisible(false);
                            }}
                            style={{
                                background: colors.primary,
                                borderColor: colors.primary,
                                width: '100%'
                            }}
                        >
                            Đăng ký
                        </Button>
                    )}
                </Space>
            )}
        </div>
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Header style={{ 
                background: colors.white, 
                padding: isMobile ? '0 20px' : '0 50px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                position: 'fixed',
                width: '100%',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 'auto',
                minHeight: 64
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    padding: isMobile ? '10px 0' : 0
                }}>
                    <img 
                        src="/banner_utt_2025.png" 
                        alt="UTT Logo" 
                        style={{ 
                            height: isMobile ? 30 : 40, 
                            marginRight: isMobile ? 8 : 16 
                        }}
                    />
                    <Title level={isMobile ? 5 : 4} style={{ 
                        margin: 0, 
                        color: colors.secondary,
                        fontSize: isMobile ? '16px' : '20px'
                    }}>
                        HỆ THỐNG ĐĂNG KÝ HỒ SƠ DỰ TUYỂN
                    </Title>
                </div>

                {isMobile ? (
                    <Button
                        icon={<MenuOutlined />}
                        onClick={() => setMenuVisible(!menuVisible)}
                        style={{ border: 'none' }}
                    />
                ) : (
                    <Space>
                        {isAuthenticated ? (
                            <>
                                <Text style={{ color: colors.secondary, marginRight: 8 }}>
                                    <UserOutlined /> {user?.name || user?.email}
                                </Text>
                                <Button 
                                    type="link" 
                                    onClick={() => navigate('/student-profile')}
                                    style={{ color: colors.secondary }}
                                >
                                    Hồ sơ dự tuyển
                                </Button>
                                <Button 
                                    danger
                                    icon={<LogoutOutlined />}
                                    onClick={handleLogout}
                                >
                                    Đăng xuất
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button 
                                    type="link" 
                                    onClick={() => navigate('/login')}
                                    style={{ color: colors.secondary }}
                                >
                                    Đăng nhập
                                </Button>
                                {hasActivePeriod && (
                                    <Button
                                        type="primary"
                                        onClick={() => navigate('/register')}
                                        style={{
                                            background: colors.primary,
                                            borderColor: colors.primary
                                        }}
                                    >
                                        Đăng ký
                                    </Button>
                                )}
                            </>
                        )}
                    </Space>
                )}
            </Header>

            {isMobile && <MobileMenu />}

            <Content style={{ 
                marginTop: isMobile ? (menuVisible ? 120 : 64) : 64,
                overflow: 'hidden'
            }}>
                <Carousel 
                    autoplay 
                    effect="fade"
                    style={{ maxHeight: isMobile ? '300px' : '500px' }}
                >
                    {carouselContent.map((item, index) => (
                        <div key={index}>
                            <div style={{
                                height: isMobile ? '300px' : '500px',
                                color: colors.white,
                                background: `linear-gradient(rgba(38, 34, 98, 0.7), rgba(38, 34, 98, 0.7)), url(${item.image}) center/cover`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                textAlign: 'center',
                                padding: isMobile ? '10px' : '0 20px'
                            }}>
                                <Title level={isMobile ? 2 : 1} style={{ 
                                    color: colors.white,
                                    fontSize: isMobile ? '24px' : '36px',
                                    marginBottom: isMobile ? '10px' : '20px'
                                }}>
                                    {item.title}
                                </Title>
                                <Paragraph style={{ 
                                    color: colors.white, 
                                    fontSize: isMobile ? '14px' : '18px',
                                    margin: isMobile ? '0 0 15px' : '0 0 24px'
                                }}>
                                    {item.description}
                                </Paragraph>
                                <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
                                    {hasActivePeriod && (
                                        <Button
                                            type="primary"
                                            size={isMobile ? 'middle' : 'large'}
                                            onClick={() => navigate('/register')}
                                            style={{
                                                background: colors.primary,
                                                borderColor: colors.primary
                                            }}
                                        >
                                            Đăng ký ngay
                                        </Button>
                                    )}
                                    <Button
                                        type="primary" 
                                        size={isMobile ? 'middle' : 'large'}
                                        onClick={() => navigate('/login')}
                                    >
                                        Đăng nhập
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    ))}
                </Carousel>
                
                <AdmissionCountdown isMobile={isMobile} period={period} loading={periodLoading} />
                <AdmissionNews />
                {/* <ProgramList />                 */}

                <div style={{ 
                    padding: isMobile ? '30px 20px' : '60px 50px', 
                    background: colors.background 
                }}>
                    <Title level={2} style={{ 
                        textAlign: 'center', 
                        marginBottom: isMobile ? 20 : 40, 
                        color: colors.secondary,
                        fontSize: isMobile ? '24px' : '30px'
                    }}>
                        Tại sao nên đăng ký vào Trường Đại học Công nghệ GTVT?
                    </Title>
                    <Row gutter={[isMobile ? 16 : 32, isMobile ? 16 : 32]} justify="center">
                        {features.map((feature, index) => (
                            <Col xs={24} sm={12} md={8} key={index}>
                                <Card 
                                    style={{ 
                                        textAlign: 'center',
                                        height: '100%',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        borderRadius: '8px'
                                    }}
                                    hoverable
                                >
                                    {feature.icon}
                                    <Title level={4} style={{ 
                                        marginTop: 16, 
                                        color: colors.secondary,
                                        fontSize: isMobile ? '18px' : '20px'
                                    }}>
                                        {feature.title}
                                    </Title>
                                    <Text style={{ 
                                        color: colors.text,
                                        fontSize: isMobile ? '14px' : '16px'
                                    }}>
                                        {feature.description}
                                    </Text>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>

                <div style={{ 
                    padding: isMobile ? '30px 20px' : '60px 10px', 
                    background: colors.white 
                }}>
                    <Title level={2} style={{ 
                        textAlign: 'center', 
                        marginBottom: isMobile ? 20 : 40, 
                        color: colors.secondary,
                        fontSize: isMobile ? '24px' : '30px'
                    }}>
                        Liên hệ
                    </Title>
                    <Row justify="center">
                        <Col xs={24} md={16} lg={12}>
                            <Card style={{ 
                                borderRadius: '8px', 
                                // boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                padding: isMobile ? '15px' : '24px'
                            }}>
                                <Space direction="vertical" size={isMobile ? 'small' : 'middle'}>
                                    <div>
                                        <PhoneOutlined style={{ marginRight: 8, color: colors.primary }} />
                                        <Text strong style={{ color: colors.secondary }}>Điện thoại:</Text>
                                        <a href="tel:02435526713" style={{ color: colors.text, textDecoration: 'none', marginLeft: 8 }}>
                                            <Text style={{ color: colors.text }}>024.3552.6713</Text>
                                        </a>
                                    </div>
                                    {/* <div>
                                        <MailOutlined style={{ marginRight: 8, color: colors.primary }} />
                                        <Text strong style={{ color: colors.secondary }}>Chatbot AI hỗ trợ tuyển sinh:</Text>
                                        <a href="http://chatbot.utt.edu.vn" style={{ color: colors.text, textDecoration: 'none', marginLeft: 8 }}>
                                            <Text style={{ color: colors.text }}>http://chatbot.utt.edu.vn</Text>
                                        </a>
                                    </div> */}
                                    <div>
                                        <FacebookOutlined style={{ marginRight: 8, color: colors.primary }} />
                                        <Text strong style={{ color: colors.secondary }}>Fanpage:</Text>
                                        <a href="https://www.facebook.com/utt.edu.vn" target="_blank" rel="noopener noreferrer" style={{ color: colors.text, textDecoration: 'none', marginLeft: 8 }}>
                                            <Text style={{ color: colors.text }}>https://www.facebook.com/utt.edu.vn</Text>
                                        </a>
                                    </div>
                                    <div>
                                        <FacebookOutlined style={{ marginRight: 8, color: colors.primary }} />
                                        <Text strong style={{ color: colors.secondary }}>Group facebook tư vấn tuyển sinh:</Text>
                                        <a href="https://www.facebook.com/groups/tuvantuyensinhdhcngtvt" target="_blank" rel="noopener noreferrer" style={{ color: colors.text, textDecoration: 'none', marginLeft: 8 }}>
                                            <Text style={{ color: colors.text }}>https://www.facebook.com/groups/tuvantuyensinhdhcngtvt</Text>
                                        </a>
                                    </div>
                                    <div>
                                        <FacebookOutlined style={{ marginRight: 8, color: colors.primary }} />
                                        <Text strong style={{ color: colors.secondary }}>Group Zalo tư vấn tuyển sinh:</Text>
                                        <a href="https://zalo.me/g/vxumny902" target="_blank" rel="noopener noreferrer" style={{ color: colors.text, textDecoration: 'none', marginLeft: 8 }}>
                                            <Text style={{ color: colors.text }}>https://zalo.me/g/vxumny902</Text>
                                        </a>
                                    </div>
                                    <div>
                                        <EnvironmentOutlined style={{ marginRight: 8, color: colors.primary }} />
                                        <Text strong style={{ color: colors.secondary }}>Địa chỉ:</Text>
                                        <Text style={{ color: colors.text, marginLeft: 8 }}>54 Triều Khúc, Thanh Liệt, Hà Nội</Text>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Chatbot />
            </Content>

            <Footer style={{ 
                textAlign: 'center', 
                background: colors.secondary, 
                color: colors.white,
                padding: isMobile ? '15px 20px' : '20px 50px',
                fontSize: isMobile ? '12px' : '14px'
            }}>
                <Text style={{ color: colors.white }}>
                    © {new Date().getFullYear()} Trường Đại học Công nghệ GTVT. All rights reserved.
                </Text>
            </Footer>
        </Layout>
    );
};

export default Home; 